/**
 * Raid adjutant — party formation owner.
 * SituationSignals → soft objective weights → per-ally slot seeds.
 * No discrete formation mode enum. Encircle geometry lives here (not kit post).
 */
import { raidCatalog, type RaidClassId } from "../../data/raid-catalog";
import { clampPointToRect, insetRect } from "../../sim";
import {
  engagementStyle,
  hostileAttackReach,
  hostileHardMinSep,
} from "./raid-engage";
import { dispositionOf } from "./raid-disposition";
import {
  applySlot,
  applySlotNearHostile,
  pointInBossDanger,
  raidPlayBounds,
  unitInBossDanger,
} from "./raid-motion";
import {
  skillZoneAdjutantSpreadFloor,
  zonesHaveBrass,
} from "./raid-skill-zone-policy";
import {
  collectRaidDangerZones,
  inEnemyMeleeDisk,
  projectClearOfDangerZones,
  raidPackFleeDist,
  type RaidDangerZone,
} from "./raid-threat";
import { querySpawnHeat, type SpawnHeatState } from "./raid-spawn-heat";
import { getActivePlayBounds } from "./sim-lab/sim-stage";
import type { RaidBoss, RaidUnit } from "./raid-types";
import {
  enemyEngageScoreBonus,
  enemyIsCombatRelevant,
  enemyMeleeRadiusPad,
} from "./raid-enemy";
import { unitHasRoleTag, unitIsStaffHealer } from "./judgment/hero-registry";

export type SituationSignals = {
  bossDominance: number;
  addPressure: number;
  spawnThreatDir: { x: number; y: number };
  aoeHazard: number;
  clumpPenalty: number;
  cornerPenalty: number;
  partyCentroid: { x: number; y: number };
  spawnHeatConfidence: number;
  spawnHeatLateral: number;
  /** True when any hard DangerZone is active (telegraph / leap). */
  zoneActive: boolean;
  /** True when a brass cone floor is painted (판결 B spread floor). */
  brassActive: boolean;
};

export type AdjutantWeights = {
  encircle: number;
  screen: number;
  spread: number;
  center: number;
};

export type AdjutantSlot = {
  unitId: number;
  x: number;
  y: number;
  depth: number;
};

export type AdjutantSnap = {
  signals: SituationSignals;
  weights: AdjutantWeights;
  centroid: { x: number; y: number };
  facing: { x: number; y: number };
  slots: readonly AdjutantSlot[];
  /** Soft focus call for engage — bond gates obedience in sticky pick. */
  priorityTargetId: number | null;
  /**
   * Wave + living boss: one tank stays on boss meat while others screen.
   * null when no split (solo tank / quiet / dormant boss).
   */
  bossAnchorTankId: number | null;
};

export type AdjutantPlanCtx = {
  boss: RaidBoss;
  allies: readonly RaidUnit[];
  enemies: readonly RaidUnit[];
  time: number;
  /** When false, dormant/hidden boss does not drive encircle. */
  includeBoss?: boolean;
  /** Experiential spawn heat for prediction. */
  spawnHeat?: SpawnHeatState | null;
};

type AdjutantTuning = {
  centroidCenterBias: number;
  backLineDist: number;
  frontLineDist: number;
  spreadPadPx: number;
  minPairDist: number;
  cornerLipPx: number;
  addSoftCap: number;
  /** Max kite DPS on full −facing backline; overflow → midDepth. */
  kiteBacklineSoftCap: number;
  /** Formation depth for kite overflow (0=front, 1=back). */
  kiteMidDepth: number;
  /** Min screen weight to assign a boss-anchor tank (≥2 tanks). */
  bossAnchorScreenMin: number;
  /** Multiplies adjutantFollowMul under screen (bond0 stays ~0). */
  screenFollowBoost: number;
  baseCenterBias: number;
  /** Max along-facing recenter fraction (wave front must not collapse south). */
  centerAlongCap: number;
  spawnHeatHalfLifeSec: number;
  spawnHeatSoftCap: number;
  spawnHeatScreenMul: number;
  zoneSpreadFloor: number;
  /** Brass cone floor — stronger party spread than generic zoneActive. */
  brassSpreadFloor: number;
  encircleSpreadSuppress: number;
  aoeMinSepMul: number;
};

/** Mapping max for adjutantFollowMul — bond50≡1, bond100 clamps here (not 1.0). */
export const ADJUTANT_FOLLOW_MUL_MAX = 1.6;

/**
 * Bond follow under wave screen / AoE spread.
 * - Mid/low bond: `followMul * (1 + boost * screen)` with screen floor.
 * - bond≈0 (follow≤0.05): still ignore screen chase seeds (lone/sniper), but
 *   take a mild **spread** floor under multi-floor AoE so personal sticky is
 *   not a death wish into brass/charge paint. Skill fantasy: dump bond → wipe
 *   on sticky focus; keep a floor for floor-clear cohesion.
 */
export function effectiveAdjutantFollow(
  followMul: number,
  screenWeight: number,
  screenBoost?: number,
  spreadWeight = 0,
): number {
  const c = raidCatalog.balance.combat as Record<string, unknown>;
  const boost =
    screenBoost ??
    (typeof c.adjutantScreenFollowBoost === "number"
      ? c.adjutantScreenFollowBoost
      : 0.75);
  const floorPer =
    typeof c.adjutantScreenFollowFloor === "number"
      ? c.adjutantScreenFollowFloor
      : 0.35;
  const spreadFloorPer =
    typeof c.adjutantSpreadFollowFloor === "number"
      ? c.adjutantSpreadFollowFloor
      : 0.45;
  const screen = clamp01(screenWeight);
  const spread = clamp01(spreadWeight);
  if (followMul <= 0.05) {
    const emergency = Math.max(0, spreadFloorPer) * spread;
    return Math.max(
      0,
      Math.min(ADJUTANT_FOLLOW_MUL_MAX, Math.max(followMul, emergency)),
    );
  }
  const scaled = followMul * (1 + Math.max(0, boost) * screen);
  const floor = Math.max(0, floorPer) * screen;
  return Math.max(
    0,
    Math.min(ADJUTANT_FOLLOW_MUL_MAX, Math.max(scaled, floor)),
  );
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/** Smoothstep 0..1. */
function smooth01(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

function norm(x: number, y: number): { x: number; y: number } {
  const len = Math.hypot(x, y);
  if (len < 1e-6) return { x: 0, y: 1 };
  return { x: x / len, y: y / len };
}

function livingAllies(allies: readonly RaidUnit[]): RaidUnit[] {
  return allies.filter((a) => a.hp > 0 && a.team === 0);
}

function livingHostiles(enemies: readonly RaidUnit[]): RaidUnit[] {
  return enemies.filter((e) => enemyIsCombatRelevant(e));
}

function tuning(): AdjutantTuning {
  const c = raidCatalog.balance.combat as Record<string, unknown>;
  const num = (k: string, fallback: number): number => {
    const v = c[k];
    return typeof v === "number" ? v : fallback;
  };
  return {
    centroidCenterBias: num("adjutantCentroidCenterBias", 0.55),
    backLineDist: num("adjutantBackLineDist", 78),
    frontLineDist: num("adjutantFrontLineDist", 36),
    spreadPadPx: num("adjutantSpreadPadPx", 42),
    minPairDist: num("adjutantMinPairDist", 54),
    cornerLipPx: num("adjutantCornerLipPx", 72),
    addSoftCap: num("adjutantAddSoftCap", 8),
    kiteBacklineSoftCap: num("adjutantKiteBacklineSoftCap", 3),
    kiteMidDepth: num("adjutantKiteMidDepth", 0.18),
    bossAnchorScreenMin: num("adjutantBossAnchorScreenMin", 0.35),
    screenFollowBoost: num("adjutantScreenFollowBoost", 0.75),
    baseCenterBias: num("adjutantBaseCenterBias", 0.12),
    centerAlongCap: num("adjutantCenterAlongCap", 0.22),
    spawnHeatHalfLifeSec: num("adjutantSpawnHeatHalfLifeSec", 10),
    spawnHeatSoftCap: num("adjutantSpawnHeatSoftCap", 6),
    spawnHeatScreenMul: num("adjutantSpawnHeatScreenMul", 1.15),
    zoneSpreadFloor: num("adjutantZoneSpreadFloor", 0.75),
    brassSpreadFloor: skillZoneAdjutantSpreadFloor("brass"),
    encircleSpreadSuppress: num("adjutantEncircleSpreadSuppress", 0.55),
    aoeMinSepMul: num("adjutantAoeMinSepMul", 1.25),
  };
}

function partyCentroidOf(allies: readonly RaidUnit[]): { x: number; y: number } {
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const a of allies) {
    sx += a.x;
    sy += a.y;
    n += 1;
  }
  if (n <= 0) {
    const b = raidPlayBounds();
    return { x: (b.xMin + b.xMax) * 0.5, y: (b.yMin + b.yMax) * 0.5 };
  }
  return { x: sx / n, y: sy / n };
}

function hostileCom(hostiles: readonly RaidUnit[]): { x: number; y: number } | null {
  if (hostiles.length === 0) return null;
  let sx = 0;
  let sy = 0;
  for (const h of hostiles) {
    sx += h.x;
    sy += h.y;
  }
  return { x: sx / hostiles.length, y: sy / hostiles.length };
}

function clumpPenaltyOf(
  allies: readonly RaidUnit[],
  minPair: number,
): number {
  if (allies.length < 2) return 0;
  let deficit = 0;
  let pairs = 0;
  for (let i = 0; i < allies.length; i++) {
    const a = allies[i]!;
    for (let j = i + 1; j < allies.length; j++) {
      const b = allies[j]!;
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      pairs += 1;
      if (d < minPair) deficit += (minPair - d) / minPair;
    }
  }
  if (pairs <= 0) return 0;
  return clamp01(deficit / pairs);
}

function cornerPenaltyOf(
  centroid: { x: number; y: number },
  lip: number,
): number {
  const b = getActivePlayBounds();
  const dx = Math.min(centroid.x - b.xMin, b.xMax - centroid.x);
  const dy = Math.min(centroid.y - b.yMin, b.yMax - centroid.y);
  const edge = Math.min(dx, dy);
  return clamp01(1 - edge / Math.max(1, lip));
}

function aoeHazardOf(
  allies: readonly RaidUnit[],
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
  includeBoss: boolean,
): number {
  if (allies.length === 0) return 0;
  let hit = 0;
  for (const a of allies) {
    if (unitInBossDanger(a, boss, includeBoss ? enemies : enemies)) hit += 1;
  }
  // Brass / yellow telegraph without feet-in still raises hazard lightly.
  const zones = collectRaidDangerZones(boss, {
    enemies,
    includeBoss,
  });
  const zoneBoost = zones.length > 0 ? 0.25 : 0;
  return clamp01(hit / allies.length + zoneBoost);
}

/** Depth 0 = front (tank/melee), 1 = back (kite/heal). RoleTags > classId. */
export function unitFormationDepth(u: RaidUnit): number {
  if (unitHasRoleTag(u, "frontline") || unitHasRoleTag(u, "tank")) return 0;
  const eng = engagementStyle(u.classId);
  if (eng === "hold") return 0.12;
  if (
    unitHasRoleTag(u, "healer") ||
    unitHasRoleTag(u, "support") ||
    unitHasRoleTag(u, "backline") ||
    eng === "skirmish"
  ) {
    return 0.72;
  }
  return 1;
}

/** Stable kite DPS order for backline softCap ranks. */
export function livingKitesRanked(
  allies: readonly RaidUnit[],
): RaidUnit[] {
  return allies
    .filter(
      (a) =>
        a.hp > 0 &&
        a.team === 0 &&
        engagementStyle(a.classId) === "kite",
    )
    .sort((a, b) => a.id - b.id);
}

/**
 * Kite depth with backline softCap — only the first `softCap` ranks get
 * full depth=1 (−facing back). Overflow sits at midDepth toward the threat
 * front (not centroid/south dead-zone).
 */
export function kiteDepthForRank(
  rankIndex: number,
  softCap = tuning().kiteBacklineSoftCap,
  midDepth = tuning().kiteMidDepth,
): number {
  if (rankIndex < Math.max(0, softCap)) return 1;
  return Math.max(0, Math.min(1, midDepth));
}

/** Per-unit depth used by planAdjutant (kite softCap aware). */
export function formationDepthForUnit(
  u: RaidUnit,
  kiteRankById: ReadonlyMap<number, number>,
): number {
  if (engagementStyle(u.classId) === "kite") {
    const rank = kiteRankById.get(u.id);
    if (rank != null) return kiteDepthForRank(rank);
  }
  return unitFormationDepth(u);
}

/**
 * Highest-bond living tank (tie → lower id). Used when screen+boss needs
 * one meat anchor while other tanks hold the wave front.
 */
export function pickBossAnchorTankId(
  allies: readonly RaidUnit[],
  screenWeight: number,
  includeBoss: boolean,
  bossAlive: boolean,
  screenMin = tuning().bossAnchorScreenMin,
): number | null {
  if (!includeBoss || !bossAlive) return null;
  if (screenWeight < screenMin) return null;
  const tanks = allies
    .filter((a) => a.hp > 0 && a.team === 0 && unitHasRoleTag(a, "tank"))
    .sort((a, b) => {
      const db = dispositionOf(b).bond - dispositionOf(a).bond;
      if (db !== 0) return db;
      return a.id - b.id;
    });
  if (tanks.length < 2) return null;
  return tanks[0]!.id;
}

/** Boss uses bar HP, not unit `hp`. */
export function bossStillFighting(boss: RaidBoss): boolean {
  return boss.barsTotal - boss.barIndex > 0;
}

/** Living tank farthest along threat facing (true front, not min-Y). */
export function frontTankAlongFacing(
  allies: readonly RaidUnit[],
  facing: { x: number; y: number },
): RaidUnit | null {
  const f = norm(facing.x, facing.y);
  let best: RaidUnit | null = null;
  let bestAlong = -Infinity;
  for (const a of allies) {
    if (a.hp <= 0 || a.classId !== "tank") continue;
    const along = a.x * f.x + a.y * f.y;
    if (along > bestAlong) {
      bestAlong = along;
      best = a;
    }
  }
  return best;
}

/**
 * Ideal healer feet: behind front tank along −facing by `healerBackline`.
 * Multiple healers get balanced lateral slots on the facing-perpendicular
 * backline (formationSlotR / min-sep spacing) — not one shared point.
 * When hostiles are present, deepen along −facing until the seed clears
 * enemy melee disks / weapon reach (tank-relative — not map-center yank).
 */
export type HealerBacklineOpts = {
  healerIndex?: number;
  healerCount?: number;
  /** Armed hostiles (esp. brutes) — deepen seed clear of melee/reach. */
  enemies?: readonly RaidUnit[];
  /** Live boss soft keep-out when encircle includes boss. */
  boss?: Pick<RaidBoss, "x" | "y" | "radius"> | null;
};

/** Keep-out radius: body melee disk ∪ hostile weapon reach + pad. */
function healerHostileKeepOut(
  e: Pick<RaidUnit, "radius" | "attackRange" | "defId">,
  healerRadius: number,
): number {
  const pad = enemyMeleeRadiusPad(e);
  const body = e.radius + pad + healerRadius;
  const weapon = e.radius + e.attackRange + pad;
  return Math.max(body, weapon);
}

/**
 * Push tank-back seed along −facing until outside hostile keep-outs and
 * farther from nearest threat than the tank. Caps depth so we stay
 * tank-relative (no arena-mid flee).
 */
function deepenHealerSeedClearOfThreat(
  seed: { x: number; y: number },
  u: RaidUnit,
  tank: RaidUnit,
  facing: { x: number; y: number },
  enemies: readonly RaidUnit[],
  boss: Pick<RaidBoss, "x" | "y" | "radius"> | null | undefined,
): { x: number; y: number } {
  const f = facing;
  const combat = raidCatalog.balance.combat as Record<string, unknown>;
  const back =
    typeof combat.healerBackline === "number" ? combat.healerBackline : 55;
  const backLine =
    typeof combat.adjutantBackLineDist === "number"
      ? combat.adjutantBackLineDist
      : 78;
  // Tank-relative cap: heal range / pack soft flee — never full map recenter.
  const maxFromTank = Math.min(
    Math.max(back * 2.4, backLine * 1.35, u.attackRange + 24),
    raidPackFleeDist("healer"),
  );

  type Disk = { x: number; y: number; r: number };
  const disks: Disk[] = [];
  for (const e of livingHostiles(enemies)) {
    disks.push({
      x: e.x,
      y: e.y,
      r: healerHostileKeepOut(e, u.radius),
    });
  }
  if (boss) {
    const t = raidCatalog.threatVolume;
    const meleePad = t.meleePad ?? 6;
    const bodyPad = t.bodyPad ?? 8;
    disks.push({
      x: boss.x,
      y: boss.y,
      r: boss.radius + bodyPad + meleePad + u.radius,
    });
  }
  if (disks.length === 0) return seed;

  let x = seed.x;
  let y = seed.y;
  for (let iter = 0; iter < 14; iter++) {
    let needPush = 0;
    let nearest: Disk | null = null;
    let nearestD = Infinity;
    for (const d of disks) {
      const dist = Math.hypot(x - d.x, y - d.y);
      if (dist < nearestD) {
        nearestD = dist;
        nearest = d;
      }
      if (dist < d.r) {
        needPush = Math.max(needPush, d.r - dist + 1);
      }
    }
    if (nearest) {
      const tankD = Math.hypot(tank.x - nearest.x, tank.y - nearest.y);
      // Prefer farther from the threat than the tank (past the front).
      if (nearestD <= tankD + 1) {
        needPush = Math.max(needPush, tankD - nearestD + back * 0.35);
      }
    }
    // Soft: still sitting in any enemy melee disk (body) → keep pushing.
    if (
      needPush < 0.5 &&
      enemies.length > 0 &&
      inEnemyMeleeDisk(x, y, u.radius, enemies)
    ) {
      needPush = Math.max(needPush, 8);
    }
    if (needPush < 0.5) break;
    const fromTank = Math.hypot(x - tank.x, y - tank.y);
    const room = maxFromTank - fromTank;
    if (room <= 0.5) break;
    const step = Math.min(needPush, room);
    x -= f.x * step;
    y -= f.y * step;
  }
  return { x, y };
}

export function healerBacklineBehindTank(
  u: RaidUnit,
  tank: RaidUnit,
  facing: { x: number; y: number },
  opts?: HealerBacklineOpts,
): { x: number; y: number } {
  const combat = raidCatalog.balance.combat as Record<string, unknown>;
  const back =
    typeof combat.healerBackline === "number" ? combat.healerBackline : 55;
  const slotTable = combat.formationSlotR as
    | Record<string, number>
    | undefined;
  const slotR = slotTable?.healer ?? 32;
  const minPair =
    typeof combat.adjutantMinPairDist === "number"
      ? combat.adjutantMinPairDist
      : 54;
  const f = norm(facing.x, facing.y);
  const ax = tank.x - f.x * back;
  const ay = tank.y - f.y * back;
  const n = Math.max(1, opts?.healerCount ?? 1);
  const i = Math.max(0, Math.min(n - 1, opts?.healerIndex ?? 0));
  let seed: { x: number; y: number };
  if (n <= 1) {
    const slotted = applySlot(ax, ay, u);
    seed = { x: slotted.tx, y: slotted.ty };
  } else {
    // Even lateral fan centered on the tank-back anchor.
    const px = -f.y;
    const py = f.x;
    const spacing = Math.max(slotR * 1.75, minPair * 0.95);
    const lat = (i - (n - 1) / 2) * spacing;
    seed = { x: ax + px * lat, y: ay + py * lat };
  }
  return deepenHealerSeedClearOfThreat(
    seed,
    u,
    tank,
    f,
    opts?.enemies ?? [],
    opts?.boss,
  );
}

/** Stable healer order for backline lateral ranks. */
export function livingHealersRanked(
  allies: readonly RaidUnit[],
): RaidUnit[] {
  return allies
    .filter((a) => unitIsStaffHealer(a))
    .sort((a, b) => a.id - b.id);
}

/** Cheap threat facing for kit micro when full adjutant snap is not yet planned. */
export function threatFacingForBackline(
  anchor: { x: number; y: number },
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
  includeBoss: boolean,
): { x: number; y: number } {
  if (includeBoss) {
    return norm(boss.x - anchor.x, boss.y - anchor.y);
  }
  const com = hostileCom(livingHostiles(enemies));
  if (com) return norm(com.x - anchor.x, com.y - anchor.y);
  // Wave default: threat from north → behind tank is south along +Y.
  return { x: 0, y: -1 };
}

export function readSituation(ctx: AdjutantPlanCtx): SituationSignals {
  const includeBoss = ctx.includeBoss !== false;
  const allies = livingAllies(ctx.allies);
  const hostiles = livingHostiles(ctx.enemies);
  const t = tuning();
  const partyCentroid = partyCentroidOf(allies);
  // softCap ~11: 4 vs 25 hostiles no longer share the same screen cliff.
  // Mild log tail past cap so W3 piles still escalate a touch past 1.0 raw.
  const addRatio = hostiles.length / Math.max(1, t.addSoftCap);
  const addPressure =
    addRatio <= 1
      ? smooth01(addRatio)
      : clamp01(1 + 0.1 * Math.min(1, Math.log2(1 + (addRatio - 1))));
  const bossDominance = includeBoss
    ? smooth01(1 - addPressure) *
      (ctx.boss.barHp > 0 || ctx.boss.barIndex < ctx.boss.barsTotal ? 1 : 0)
    : 0;

  const heat = querySpawnHeat(ctx.spawnHeat, ctx.time, {
    halfLifeSec: t.spawnHeatHalfLifeSec,
    softCap: t.spawnHeatSoftCap,
  });

  const hCom = hostileCom(hostiles);
  let liveX = 0;
  let liveY = -1;
  if (hCom) {
    liveX = hCom.x - partyCentroid.x;
    liveY = hCom.y - partyCentroid.y;
  } else if (includeBoss) {
    liveX = ctx.boss.x - partyCentroid.x;
    liveY = ctx.boss.y - partyCentroid.y;
  }
  const liveDir = norm(liveX, liveY);

  let spawnThreatDir = liveDir;
  if (heat.com && heat.confidence > 0.05) {
    const hx = heat.com.x - partyCentroid.x;
    const hy = heat.com.y - partyCentroid.y;
    const heatDir = norm(hx, hy);
    const k = heat.confidence;
    spawnThreatDir = norm(
      liveDir.x * (1 - k) + heatDir.x * k,
      liveDir.y * (1 - k) + heatDir.y * k,
    );
  }

  const zones = collectRaidDangerZones(ctx.boss, {
    enemies: ctx.enemies,
    includeBoss,
  });
  const brassActive = zonesHaveBrass(zones);

  return {
    bossDominance,
    addPressure,
    spawnThreatDir,
    aoeHazard: aoeHazardOf(allies, ctx.boss, ctx.enemies, includeBoss),
    clumpPenalty: clumpPenaltyOf(allies, t.minPairDist),
    cornerPenalty: cornerPenaltyOf(partyCentroid, t.cornerLipPx),
    partyCentroid,
    spawnHeatConfidence: heat.confidence,
    spawnHeatLateral: heat.lateralSigma,
    zoneActive: zones.length > 0,
    brassActive,
  };
}

export function weightsFromSignals(s: SituationSignals): AdjutantWeights {
  const t = tuning();
  let encircle = s.bossDominance;
  const screen = Math.max(s.addPressure, s.spawnHeatConfidence);
  let spread = Math.max(s.aoeHazard, s.clumpPenalty);
  if (s.zoneActive) {
    spread = Math.max(spread, t.zoneSpreadFloor + 0.25 * s.aoeHazard);
  }
  // 판결 B: brass cone → stronger spread floor (wipe softener spacing).
  if (s.brassActive) {
    spread = Math.max(spread, t.brassSpreadFloor);
  }
  // Telegraph: spread fully wins. Clump-only: milder so boss ring look remains.
  const suppressScale =
    s.zoneActive || s.brassActive || s.aoeHazard >= 0.4 ? 1 : 0.35;
  encircle *= 1 - t.encircleSpreadSuppress * spread * suppressScale;
  const screenOut = screen * (1 - 0.25 * spread * suppressScale);
  // Center rises on lip/clump — not a constant south pull during wave front.
  const centerGate = Math.max(s.cornerPenalty, s.clumpPenalty * 0.85);
  const center = Math.max(t.baseCenterBias, centerGate);
  return { encircle, screen: screenOut, spread, center };
}

function blendFacing(
  s: SituationSignals,
  w: AdjutantWeights,
  boss: RaidBoss,
  includeBoss: boolean,
): { x: number; y: number } {
  const fromBoss = includeBoss
    ? norm(boss.x - s.partyCentroid.x, boss.y - s.partyCentroid.y)
    : { x: 0, y: -1 };
  const ax = w.encircle;
  const bx = w.screen;
  const sum = ax + bx;
  if (sum < 1e-6) return s.spawnThreatDir;
  return norm(
    (fromBoss.x * ax + s.spawnThreatDir.x * bx) / sum,
    (fromBoss.y * ax + s.spawnThreatDir.y * bx) / sum,
  );
}

/**
 * Recenter party origin: lateral (perp to facing) first; along-facing capped
 * so wave screens do not abandon the threat front for arena mid.
 */
function facingGatedCentroid(
  raw: { x: number; y: number },
  facing: { x: number; y: number },
  centerW: number,
  signals: SituationSignals,
): { x: number; y: number } {
  const t = tuning();
  const b = getActivePlayBounds();
  const cx = (b.xMin + b.xMax) * 0.5;
  const cy = (b.yMin + b.yMax) * 0.5;
  const gate = Math.max(signals.cornerPenalty, signals.clumpPenalty);
  const k = clamp01(centerW) * t.centroidCenterBias * (0.35 + 0.65 * gate);
  if (k < 1e-4) return raw;

  const dx = cx - raw.x;
  const dy = cy - raw.y;
  const along = dx * facing.x + dy * facing.y;
  const latX = dx - facing.x * along;
  const latY = dy - facing.y * along;
  const alongCap = t.centerAlongCap * Math.abs(along);
  const alongClamped =
    Math.sign(along || 1) * Math.min(Math.abs(along), alongCap);
  // Prefer lateral recenter; mild along only when lip/clump is high.
  const alongUse = alongClamped * (0.25 + 0.75 * gate);
  return {
    x: raw.x + (latX + facing.x * alongUse) * k,
    y: raw.y + (latY + facing.y * alongUse) * k,
  };
}

function bossEngageRing(u: RaidUnit, boss: RaidBoss): number {
  const combat = raidCatalog.balance.combat as Record<string, unknown>;
  const bossReach = hostileAttackReach(u, boss);
  const hardMin = hostileHardMinSep(u, boss);
  const epsRaw = combat.bodySepReachEps;
  const reachEps = typeof epsRaw === "number" ? epsRaw : 2;
  let ring = Math.min(
    Math.max(bossReach * 0.92, hardMin),
    Math.max(hardMin, bossReach - reachEps),
  );
  if (unitHasRoleTag(u, "tank") || unitHasRoleTag(u, "frontline")) {
    const gap =
      typeof combat.tankMeleeGap === "number" ? combat.tankMeleeGap : 8;
    const surface = boss.radius + u.radius + gap;
    ring = Math.min(
      Math.max(surface, hardMin),
      Math.max(hardMin, bossReach - reachEps),
    );
  } else if (
    unitHasRoleTag(u, "healer") ||
    unitHasRoleTag(u, "support") ||
    unitHasRoleTag(u, "backline")
  ) {
    const back =
      typeof combat.healerBackline === "number" ? combat.healerBackline : 55;
    ring = Math.min(bossReach * 0.98, Math.max(ring, hardMin + back * 0.35));
  } else {
    // dps / focus_dps / unknown job tags
    ring = Math.min(bossReach * 0.88, Math.max(hardMin, bossReach * 0.85));
  }
  return ring;
}

/** Encircle objective — same family as former kit applySlotNearHostile / slotBoss. */
export function encircleSlotFor(
  u: RaidUnit,
  boss: RaidBoss,
  time: number,
): { x: number; y: number } {
  const ring = bossEngageRing(u, boss);
  const slotted = applySlotNearHostile(u.x, u.y, u, boss, ring, { time });
  return { x: slotted.tx, y: slotted.ty };
}

/**
 * Even arc on boss ring by stable unit rank — breaks 12-o'clock stack when
 * spread/clump asks for redistribution.
 */
function encircleArcSlotFor(
  u: RaidUnit,
  boss: RaidBoss,
  allies: readonly RaidUnit[],
): { x: number; y: number } {
  const ring = bossEngageRing(u, boss);
  const ranked = [...allies].sort((a, b) => a.id - b.id);
  const idx = Math.max(
    0,
    ranked.findIndex((a) => a.id === u.id),
  );
  const n = Math.max(1, ranked.length);
  // Spread around full circle; bias start so first tank isn't always due-north.
  const ang = -Math.PI / 2 + ((idx + 0.5) / n) * Math.PI * 2;
  return {
    x: boss.x + Math.cos(ang) * ring,
    y: boss.y + Math.sin(ang) * ring,
  };
}

function screenSlotFor(
  u: RaidUnit,
  centroid: { x: number; y: number },
  facing: { x: number; y: number },
  depth: number,
  lateralScale = 1,
): { x: number; y: number } {
  const t = tuning();
  const along = t.frontLineDist * (1 - depth) - t.backLineDist * depth;
  const baseLat =
    ((u.id % 9) / 9 - 0.5) *
    (raidCatalog.balance.combat.formationSlotR[u.classId as RaidClassId] ??
      32) *
    2.2;
  const lat = baseLat * Math.max(1, lateralScale);
  const px = -facing.y;
  const py = facing.x;
  return {
    x: centroid.x + facing.x * along + px * lat,
    y: centroid.y + facing.y * along + py * lat,
  };
}

function spreadOffsetFor(
  u: RaidUnit,
  allies: readonly RaidUnit[],
  pad: number,
): { x: number; y: number } {
  let rx = 0;
  let ry = 0;
  let n = 0;
  for (const a of allies) {
    if (a.id === u.id || a.hp <= 0) continue;
    const dx = u.x - a.x;
    const dy = u.y - a.y;
    const d = Math.hypot(dx, dy);
    if (d < 1e-3) {
      rx += Math.cos(u.id * 1.7);
      ry += Math.sin(u.id * 1.7);
      n += 1;
      continue;
    }
    if (d < pad * 2.5) {
      const push = (pad * 2.5 - d) / (pad * 2.5);
      rx += (dx / d) * push;
      ry += (dy / d) * push;
      n += 1;
    }
  }
  if (n <= 0) return { x: 0, y: 0 };
  return { x: (rx / n) * pad, y: (ry / n) * pad };
}

function clampSlot(
  u: RaidUnit,
  x: number,
  y: number,
): { x: number; y: number } {
  const b = insetRect(raidPlayBounds(), u.radius);
  return clampPointToRect(x, y, b);
}

/**
 * Greedy min-sep on drafted slots — push along facing-perpendicular so
 * screen depth stays, pairwise gaps open.
 */
function applyMinSepPass(
  drafts: AdjutantSlot[],
  facing: { x: number; y: number },
  minDist: number,
  alliesById: Map<number, RaidUnit>,
): AdjutantSlot[] {
  const px = -facing.y;
  const py = facing.x;
  const out = drafts.map((s) => ({ ...s }));
  for (let iter = 0; iter < 4; iter++) {
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i]!;
        const b = out[j]!;
        let dx = a.x - b.x;
        let dy = a.y - b.y;
        let d = Math.hypot(dx, dy);
        if (d >= minDist) continue;
        if (d < 1e-3) {
          const side = a.unitId < b.unitId ? 1 : -1;
          dx = px * side;
          dy = py * side;
          d = 1;
        }
        const need = (minDist - d) * 0.5;
        // Prefer lateral separation (perp facing); keep a bit of radial push.
        const nx = dx / d;
        const ny = dy / d;
        const lat = nx * px + ny * py;
        const latSign = lat >= 0 ? 1 : -1;
        const pushX = px * latSign * need * 0.85 + nx * need * 0.15;
        const pushY = py * latSign * need * 0.85 + ny * need * 0.15;
        a.x += pushX;
        a.y += pushY;
        b.x -= pushX;
        b.y -= pushY;
      }
    }
  }
  for (const s of out) {
    const u = alliesById.get(s.unitId);
    if (!u) continue;
    const c = clampSlot(u, s.x, s.y);
    s.x = c.x;
    s.y = c.y;
  }
  return out;
}

/**
 * Soft collective focus: prefer armed brutes, else nearest living hostile to
 * party centroid. Shared with engage pick (bond gates obedience).
 */
export function resolveAdjutantPriorityTargetId(
  allies: readonly RaidUnit[],
  enemies: readonly RaidUnit[],
  facing?: { x: number; y: number } | null,
): number | null {
  const living = livingHostiles(enemies);
  if (living.length === 0) return null;
  const party = livingAllies(allies);
  const anchor =
    party.length > 0
      ? partyCentroidOf(party)
      : { x: living[0]!.x, y: living[0]!.y };
  const fx = facing?.x ?? 0;
  const fy = facing?.y ?? -1;
  const score = (e: RaidUnit): number => {
    const dx = e.x - anchor.x;
    const dy = e.y - anchor.y;
    const along = dx * fx + dy * fy;
    const dist = Math.hypot(dx, dy);
    return enemyEngageScoreBonus(e) + along * 1.2 - dist;
  };
  let best = living[0]!;
  let bestS = score(best);
  for (let i = 1; i < living.length; i++) {
    const e = living[i]!;
    const s = score(e);
    if (s > bestS) {
      bestS = s;
      best = e;
    }
  }
  return best.id;
}

/**
 * Plan party slots for this tick. Pure given living units + boss pose (+ heat).
 */
export function planAdjutant(ctx: AdjutantPlanCtx): AdjutantSnap {
  const includeBoss = ctx.includeBoss !== false;
  const allies = livingAllies(ctx.allies);
  const signals = readSituation(ctx);
  const weights = weightsFromSignals(signals);
  const facing = blendFacing(signals, weights, ctx.boss, includeBoss);
  const centroid = facingGatedCentroid(
    signals.partyCentroid,
    facing,
    weights.center,
    signals,
  );
  const t = tuning();
  const lateralScale =
    1 +
    t.spawnHeatScreenMul *
      clamp01(signals.spawnHeatLateral / Math.max(40, t.minPairDist * 3));

  const wPos =
    weights.encircle + weights.screen + Math.max(weights.spread, 0.08);
  const wE = weights.encircle / wPos;
  const wS = weights.screen / wPos;
  const wSp = Math.max(weights.spread, 0.08) / wPos;

  const redistributeArc =
    includeBoss &&
    (weights.spread >= 0.4 || signals.clumpPenalty >= 0.35 || signals.zoneActive);

  const zones = collectRaidDangerZones(ctx.boss, {
    enemies: ctx.enemies,
    includeBoss,
  });

  const drafts: AdjutantSlot[] = [];
  const byId = new Map(allies.map((u) => [u.id, u] as const));
  const frontTankU = frontTankAlongFacing(allies, facing);
  const healers = livingHealersRanked(allies);
  const kites = livingKitesRanked(allies);
  const kiteRankById = new Map(kites.map((u, i) => [u.id, i] as const));
  const bossAnchorTankId = pickBossAnchorTankId(
    allies,
    weights.screen,
    includeBoss,
    bossStillFighting(ctx.boss),
  );
  for (const u of allies) {
    const depth = formationDepthForUnit(u, kiteRankById);
    const isBossAnchor = bossAnchorTankId != null && u.id === bossAnchorTankId;
    let enc: { x: number; y: number };
    let scr: { x: number; y: number };
    // Healers: adjutant owns tank-relative backline (encircle + screen), not
    // centroid depth alone / hard south. Multi-healers fan laterally.
    if (unitIsStaffHealer(u) && frontTankU) {
      const idx = healers.findIndex((h) => h.id === u.id);
      const behind = healerBacklineBehindTank(u, frontTankU, facing, {
        healerIndex: Math.max(0, idx),
        healerCount: healers.length,
        enemies: ctx.enemies,
        boss: includeBoss ? ctx.boss : null,
      });
      enc = behind;
      scr = behind;
    } else if (!includeBoss) {
      enc = screenSlotFor(u, centroid, facing, depth, lateralScale);
      scr = enc;
    } else if (redistributeArc) {
      enc = encircleArcSlotFor(u, ctx.boss, allies);
      scr = screenSlotFor(u, centroid, facing, depth, lateralScale);
    } else {
      enc = encircleSlotFor(u, ctx.boss, ctx.time);
      scr = screenSlotFor(u, centroid, facing, depth, lateralScale);
    }
    // Boss-anchor tank: keep meat on encircle; do not march full screen north.
    if (isBossAnchor && includeBoss) {
      scr = enc;
    }
    const localWE = isBossAnchor ? wE + wS * 0.75 : wE;
    const localWS = isBossAnchor ? wS * 0.25 : wS;
    // Wave screen: clump-spread must not erase facing depth (softCap back
    // vs midDepth toward threat). Otherwise overflow kites wash to centroid
    // / south and die under north spawn piles.
    const depthPreserve = clamp01(localWS * 1.25);
    const useWSp = wSp * (1 - 0.75 * depthPreserve);
    const localSum = Math.max(1e-6, localWE + localWS + useWSp);
    const spr = spreadOffsetFor(u, allies, t.spreadPadPx);
    let rawX =
      (enc.x * localWE + scr.x * localWS + (u.x + spr.x) * useWSp) / localSum;
    let rawY =
      (enc.y * localWE + scr.y * localWS + (u.y + spr.y) * useWSp) / localSum;
    if (
      engagementStyle(u.classId) === "kite" &&
      depthPreserve > 0.2 &&
      (localWS > 0.05 || !includeBoss)
    ) {
      // Re-apply screen along so midDepth stays threat-forward vs depth=1.
      const fx = facing.x;
      const fy = facing.y;
      const alongIdeal =
        (scr.x - centroid.x) * fx + (scr.y - centroid.y) * fy;
      const alongRaw = (rawX - centroid.x) * fx + (rawY - centroid.y) * fy;
      const dAlong = alongIdeal - alongRaw;
      rawX += fx * dAlong;
      rawY += fy * dAlong;
    }
    const clamped = clampSlot(u, rawX, rawY);
    drafts.push({
      unitId: u.id,
      x: clamped.x,
      y: clamped.y,
      depth,
    });
  }

  // Rescue seed bias is applied once in healerSupport kit blend
  // (raid-kit-interp) — not here. Double application (planAdjutant + kit)
  // regressed Soft plateau (N=5 both 0.4→ N=10 0.9). Claim B chase still banned.

  const sepMul =
    signals.zoneActive ||
    signals.brassActive ||
    signals.aoeHazard >= 0.35
      ? t.aoeMinSepMul
      : 1;
  const minSep = t.minPairDist * sepMul;
  let slotsRaw = applyMinSepPass(drafts, facing, minSep, byId);
  // Lip pull then min-sep again so recenter does not re-clump.
  for (const s of slotsRaw) {
    const u = byId.get(s.unitId);
    if (!u) continue;
    const pulled = pullSlotOffLip(u, s.x, s.y, signals.partyCentroid);
    s.x = pulled.x;
    s.y = pulled.y;
  }
  slotsRaw = applyMinSepPass(slotsRaw, facing, minSep, byId);
  if (zones.length > 0) {
    for (const s of slotsRaw) {
      const u = byId.get(s.unitId);
      if (!u) continue;
      const cleared = pushSlotClearOfZones(u, s.x, s.y, zones);
      s.x = cleared.x;
      s.y = cleared.y;
    }
    slotsRaw = applyMinSepPass(slotsRaw, facing, minSep, byId);
  }
  return {
    signals,
    weights,
    centroid,
    facing,
    slots: slotsRaw,
    priorityTargetId: resolveAdjutantPriorityTargetId(
      allies,
      ctx.enemies,
      facing,
    ),
    bossAnchorTankId,
  };
}

/** Push slot out of active danger volumes (shared projection + slot clamp). */
function pushSlotClearOfZones(
  u: RaidUnit,
  x0: number,
  y0: number,
  zones: readonly RaidDangerZone[],
): { x: number; y: number } {
  const cleared = projectClearOfDangerZones(x0, y0, u.radius, zones, 4);
  return clampSlot(u, cleared.x, cleared.y);
}

/** Keep formation seeds off playBounds lips (shared axis with spaceOutBossGoal). */
function pullSlotOffLip(
  u: RaidUnit,
  x: number,
  y: number,
  anchor: { x: number; y: number },
): { x: number; y: number } {
  const bounds = raidPlayBounds();
  const edgeProx = Math.min(
    x - bounds.xMin - u.radius,
    bounds.xMax - u.radius - x,
    y - bounds.yMin - u.radius,
    bounds.yMax - u.radius - y,
  );
  if (edgeProx >= 36) return { x, y };
  const pull = (36 - Math.max(0, edgeProx)) / 36;
  const t = 0.5 + 0.4 * pull;
  return clampSlot(
    u,
    x + (anchor.x - x) * t,
    y + (anchor.y - y) * (t * 0.55),
  );
}

export function slotFor(
  snap: AdjutantSnap | null | undefined,
  unitId: number,
): AdjutantSlot | null {
  if (!snap) return null;
  return snap.slots.find((s) => s.unitId === unitId) ?? null;
}

/** Full consume: boss encircle / kite / chase / hold gap. */
export function shouldConsumeAdjutantSeed(args: {
  hasGestureRef: boolean;
  positionMode: string;
  targetIsBoss: boolean;
}): boolean {
  if (args.hasGestureRef) return false;
  const mode = args.positionMode;
  if (
    mode === "retreatToHealer" ||
    mode === "spaceFromBoss" ||
    mode === "healerSupport"
  ) {
    return false;
  }
  if (
    mode === "bossRing" ||
    mode === "holdMeleeGap" ||
    mode === "kiteFromTarget" ||
    mode === "chaseTarget"
  ) {
    return true;
  }
  return false;
}

/** Soft lateral for tank pack modes (chase/space/stand) — keep kit intent. */
export function shouldSoftLateralAdjutantSeed(args: {
  hasGestureRef: boolean;
  positionMode: string;
}): boolean {
  if (args.hasGestureRef) return false;
  const mode = args.positionMode;
  return (
    mode === "spaceFromThreat" ||
    mode === "chaseNearestPackHostile" ||
    mode === "standOrChase"
  );
}

/** Soft blend for gambit space kits that used to slotNearBoss.
 * Spread/center only — never pull space feet back onto the encircle ring.
 */
export function blendSpaceGoalWithAdjutant(
  tx: number,
  ty: number,
  seed: AdjutantSlot,
  weights: AdjutantWeights,
): { tx: number; ty: number } {
  const t = clamp01(
    0.12 +
      0.4 * weights.spread +
      0.3 * weights.center +
      0.35 * weights.screen,
  );
  return {
    tx: tx + (seed.x - tx) * t,
    ty: ty + (seed.y - ty) * t,
  };
}

/**
 * Keep kit radial stand (measure/weapon band); take party arc from adjutant
 * by blending bearing toward the seed around `hostile` (boss only).
 */
export function blendKitRingWithAdjutantSeed(
  kitTx: number,
  kitTy: number,
  seed: AdjutantSlot,
  hostile: { x: number; y: number },
  bearingBlend = 0.62,
): { tx: number; ty: number } {
  const kitDx = kitTx - hostile.x;
  const kitDy = kitTy - hostile.y;
  const kitR = Math.hypot(kitDx, kitDy);
  if (kitR < 1e-3) {
    return { tx: seed.x, ty: seed.y };
  }
  const seedDx = seed.x - hostile.x;
  const seedDy = seed.y - hostile.y;
  const seedR = Math.hypot(seedDx, seedDy) || 1;
  const kx = kitDx / kitR;
  const ky = kitDy / kitR;
  const sx = seedDx / seedR;
  const sy = seedDy / seedR;
  const bx = kx + (sx - kx) * bearingBlend;
  const by = ky + (sy - ky) * bearingBlend;
  const bl = Math.hypot(bx, by) || 1;
  return {
    tx: hostile.x + (bx / bl) * kitR,
    ty: hostile.y + (by / bl) * kitR,
  };
}

/**
 * World-space lerp toward adjutant seed (add engage / pack soft-lateral).
 * Spread/clump/center drive strength; screen weighs in under wave so bond
 * follow actually closes formation holes (sticky AA still plant-gated in kit).
 */
export function worldLerpTowardAdjutantSeed(
  tx: number,
  ty: number,
  seed: AdjutantSlot,
  weights: AdjutantWeights,
  baseK: number,
  followMul = 1,
): { tx: number; ty: number } {
  const pressure = Math.max(
    weights.spread,
    weights.center * 0.55,
    weights.screen * 0.55,
  );
  const k = clamp01(
    (baseK * (0.2 + 0.8 * pressure) + 0.12 * weights.spread) *
      Math.max(0, followMul),
  );
  if (k < 0.08) return { tx, ty };
  return {
    tx: tx + (seed.x - tx) * k,
    ty: ty + (seed.y - ty) * k,
  };
}

/**
 * Soft-lateral only: keep flee along-facing; pull toward seed on
 * facing-perpendicular. Prevents south backline seeds from cancelling pack kite.
 */
export function worldLerpAdjutantSeedLateralOnly(
  tx: number,
  ty: number,
  seed: AdjutantSlot,
  facing: { x: number; y: number },
  weights: AdjutantWeights,
  baseK: number,
  followMul = 1,
): { tx: number; ty: number } {
  const f = norm(facing.x, facing.y);
  let dx = seed.x - tx;
  let dy = seed.y - ty;
  const along = dx * f.x + dy * f.y;
  dx -= f.x * along;
  dy -= f.y * along;
  const pressure = Math.max(
    weights.spread,
    weights.center * 0.55,
    weights.screen * 0.55,
  );
  const k = clamp01(
    (baseK * (0.2 + 0.8 * pressure) + 0.12 * weights.spread) *
      Math.max(0, followMul),
  );
  if (k < 0.08) return { tx, ty };
  return {
    tx: tx + dx * k,
    ty: ty + dy * k,
  };
}

/** Test/helper: danger probe without unused import lint when zones empty. */
export function adjutantPointClearsHazard(
  x: number,
  y: number,
  radius: number,
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
): boolean {
  return !pointInBossDanger(x, y, radius, boss, enemies);
}
